import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const NavigationArrows = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Use refs for the actual state to avoid timing issues
  // Each entry: { path: string, scrollY: number }
  const historyStackRef = useRef([]);
  const currentIndexRef = useRef(-1);
  const isNavigatingRef = useRef(false);
  const targetIndexRef = useRef(-1);
  const shouldRestoreScrollRef = useRef(false);
  const targetScrollRef = useRef(0);

  // State just for triggering re-renders
  const [, forceUpdate] = useState(0);

  // Save current scroll position to the current history entry
  const saveScrollPosition = useCallback(() => {
    const index = currentIndexRef.current;
    if (index >= 0 && index < historyStackRef.current.length) {
      historyStackRef.current[index].scrollY = window.scrollY;
    }
  }, []);

  // Track navigation history.
  // useLayoutEffect (not useEffect) so the scroll restore below runs
  // synchronously right after the new page's DOM is committed but before
  // the browser paints - a plain useEffect fires after paint, so the
  // browser would show the fresh page at scrollY 0 for a frame and then
  // visibly jump to the restored position.
  useLayoutEffect(() => {
    const currentPath = location.pathname + location.search;

    // If we're navigating via back/forward buttons
    if (isNavigatingRef.current) {
      currentIndexRef.current = targetIndexRef.current;
      isNavigatingRef.current = false;

      if (shouldRestoreScrollRef.current) {
        window.scrollTo(0, targetScrollRef.current);
        shouldRestoreScrollRef.current = false;
      }

      forceUpdate(n => n + 1);
      return;
    }

    // Save scroll position of current page before adding new entry
    saveScrollPosition();

    const stack = historyStackRef.current;
    const index = currentIndexRef.current;

    // If we're not at the end, truncate forward history
    if (index >= 0 && index < stack.length - 1) {
      historyStackRef.current = stack.slice(0, index + 1);
    }

    // Don't add duplicate consecutive entries
    const currentStack = historyStackRef.current;
    if (currentStack.length > 0 && currentStack[currentStack.length - 1].path === currentPath) {
      return;
    }

    // Reuse this page's last known scroll position if we've been here
    // before (e.g. switching Pro -> Data -> Pro via nav links/tabs, not
    // just this component's own back/forward buttons), instead of always
    // resetting to the top.
    const priorEntry = [...currentStack].reverse().find(entry => entry.path === currentPath);
    const restoreScrollY = priorEntry ? priorEntry.scrollY : 0;

    historyStackRef.current = [...currentStack, { path: currentPath, scrollY: restoreScrollY }];
    currentIndexRef.current = historyStackRef.current.length - 1;

    window.scrollTo(0, restoreScrollY);

    forceUpdate(n => n + 1);
  }, [location.pathname, location.search, saveScrollPosition]);

  // Save scroll position when user scrolls
  useEffect(() => {
    const handleScroll = () => {
      const index = currentIndexRef.current;
      if (index >= 0 && index < historyStackRef.current.length) {
        historyStackRef.current[index].scrollY = window.scrollY;
      }
    };

    // Debounce scroll saving
    let scrollTimeout;
    const debouncedScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const handleBack = useCallback(() => {
    const index = currentIndexRef.current;
    if (index > 0) {
      // Save current scroll position before leaving
      saveScrollPosition();

      const targetEntry = historyStackRef.current[index - 1];
      isNavigatingRef.current = true;
      targetIndexRef.current = index - 1;
      shouldRestoreScrollRef.current = true;
      targetScrollRef.current = targetEntry.scrollY || 0;
      navigate(targetEntry.path);
    }
  }, [navigate, saveScrollPosition]);

  const handleForward = useCallback(() => {
    const index = currentIndexRef.current;
    const stackLength = historyStackRef.current.length;
    if (index >= 0 && index < stackLength - 1) {
      // Save current scroll position before leaving
      saveScrollPosition();

      const targetEntry = historyStackRef.current[index + 1];
      isNavigatingRef.current = true;
      targetIndexRef.current = index + 1;
      shouldRestoreScrollRef.current = true;
      targetScrollRef.current = targetEntry.scrollY || 0;
      navigate(targetEntry.path);
    }
  }, [navigate, saveScrollPosition]);

  // Calculate visibility based on refs
  const canGoBack = currentIndexRef.current > 0;
  const canGoForward = currentIndexRef.current >= 0 &&
                       currentIndexRef.current < historyStackRef.current.length - 1;

  // Base button style (same as scroll button)
  const baseButtonStyle = {
    position: 'fixed',
    top: '70px',
    zIndex: 1000,
    backgroundColor: 'transparent',
    color: '#999',
    border: 'none',
    cursor: 'pointer',
    fontSize: '28px',
    padding: '10px',
    transition: 'opacity 0.3s'
  };

  const backButtonStyle = {
    ...baseButtonStyle,
    left: '20px',
    display: canGoBack ? 'block' : 'none'
  };

  const forwardButtonStyle = {
    ...baseButtonStyle,
    left: '55px',
    display: canGoForward ? 'block' : 'none'
  };

  return (
    <>
      <button
        style={backButtonStyle}
        onClick={handleBack}
        aria-label="Go back"
        title="Go back"
      >
        <span className="fa fa-arrow-left"></span>
      </button>
      <button
        style={forwardButtonStyle}
        onClick={handleForward}
        aria-label="Go forward"
        title="Go forward"
      >
        <span className="fa fa-arrow-right"></span>
      </button>
    </>
  );
};

export default NavigationArrows;
